import type { ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  error?: string | null;
}

/** Native <select> kept native — no custom listbox, no portal, no animation. */
export function Select({
  label,
  error,
  className,
  id,
  children,
  ...rest
}: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={selectId} className="text-xs font-medium text-ink-muted">
          {label}
        </label>
      ) : null}
      <div className="relative">
        <select
          id={selectId}
          aria-invalid={error ? true : undefined}
          className={cn(
            "h-8 w-full appearance-none rounded-md border bg-surface pl-2.5 pr-7",
            "text-sm text-ink transition-colors",
            "disabled:bg-surface-sunken disabled:text-ink-faint",
            error ? "border-violation" : "border-border-strong",
            className,
          )}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-faint"
        />
      </div>
      {error ? <p className="text-2xs text-violation">{error}</p> : null}
    </div>
  );
}
